import debounce from 'lodash/debounce';
import { ComponentType, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectIsWhatsAppInfobipLocalesEnabled,
  selectLocaleImprovementsEnabled,
  selectNextBestActionEnabled,
} from 'core/userContext';
import { LOCALE_CATEGORY_LABELS, LocaleCategory } from 'modules/campaigns/lib/constants';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { useAddLocale } from 'modules/templateEditor/hooks/useAddLocale';
import { useFilterLocales } from 'modules/templateEditor/hooks/useFilterLocales';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { ActionMenu } from 'ui/actionMenu';
import { MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Delete, Settings } from 'ui/icons';
import {
  ALIGNMENT,
  BASIC_SELECT_SIZES,
  BasicFilterSelect,
  OnSelectHandler,
  SelectValueDisplayProps,
} from 'ui/selects';
import { LeftTooltip, Tooltip } from 'ui/tooltip';

import { TemplateType } from 'constants/templateTypes';
import { MetaApprovalStatusDefinition } from 'modules/graphql';
import styled from 'styled-components';
import { NewTabLink } from 'ui/links/NewTabLink';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useDeleteLocale } from '../../hooks/useDeleteLocale';
import { useTranslations } from '../../hooks/useTranslations';
import { useUpdateDefaultLocale } from '../../hooks/useUpdateDefaultLocale';
import { DefaultLocaleModal, Variables } from '../../modals/DefaultLocaleModal';
import { DeleteLocaleModal } from '../../modals/DeleteLocaleModal';
import { useLocaleActionMenuOptions, useLocaleOptions, useTooltipErrorContent } from './hooks';
import { ComboButton, StyledOverflowButton, SwitchContainer } from './LocaleSwitcher.styled';
import { getLocalesErrorCount } from './LocaleSwitcher.utils';
import { LocaleSwitcherTrigger } from './LocaleSwitcherTrigger';

const PROJECT_SETTINGS_URL = '/settings/project';
export const CONFIGURE_LOCALE_BTN_TEST_ID = 'configure-locale-btn';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledNewTabLink = styled(NewTabLink)`
  text-transform: none;
`;

const getCategoryLabels = (isWhatsAppInfobipLocalesEnabled: boolean) =>
  isWhatsAppInfobipLocalesEnabled
    ? {
        ...LOCALE_CATEGORY_LABELS,
        [LocaleCategory.NotAvailableForWhatsApp]: (
          <Wrapper>
            Not available for WhatsApp
            <StyledNewTabLink href={PROJECT_SETTINGS_URL} data-test={CONFIGURE_LOCALE_BTN_TEST_ID}>
              Configure
            </StyledNewTabLink>
          </Wrapper>
        ),
      }
    : LOCALE_CATEGORY_LABELS;

export type LocaleSwitcherProps = {
  horizontalAlignment?: ALIGNMENT;
  isReadOnly?: boolean;
  hideErrorCount?: boolean;
  isSwitcherDisabled?: boolean;
};

export const LocaleSwitcher = ({
  horizontalAlignment,
  isReadOnly = false,
  hideErrorCount = false,
  isSwitcherDisabled = false,
}: LocaleSwitcherProps) => {
  const dispatch = useDispatch();
  const { t, ready } = useTranslations();
  const isNextBestActionEnabled = useSelector(selectNextBestActionEnabled);
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const isWhatsAppInfobipLocalesEnabled = useSelector(selectIsWhatsAppInfobipLocalesEnabled);

  const deleteLocale = useDeleteLocale();
  const { allContentErrors, revalidate, nbaLocalesErrors } = useErrorValidationContext();
  const { addLocale } = useAddLocale();
  const { isDirty } = useEditableFieldsContext();
  const { isLoading, isAssociatedWithActiveExperiment, templateType } = useTemplateQueryContext();
  const {
    refetch: refetchLocales,
    activeTemplateLocales,
    allProjectLocales,
    templateDefaultLocale,
    activeWhatsAppTemplateLocales,
    invalidWhatsAppLocales,
    validWhatsAppLocales,
  } = useLocalesQueryContext();
  const { isTemplateDefaultLocaleModifiable } = useLocaleSettingsContext();
  const {
    locale,
    setLocale,
    templateId,
    experimentFlow: isExperimentFlow,
  } = useQueryParamContext();

  const [showDeleteModal, handleShowDeleteModal] = useState(false);
  const [showDefaultLocaleModal, handleShowDefaultLocaleModal] = useState(false);
  const isDisabled = isDirty;
  const projectDefaultLocaleTag = allProjectLocales[0]?.tag || '';
  const templateDefaultLocaleTag = templateDefaultLocale.tag;
  const projectDefaultLocaleExistsInTemplate = !!activeTemplateLocales.find(
    (tl) => tl.tag === projectDefaultLocaleTag,
  );
  const defaultSuffix = ready ? (t('label.default') as string) : 'Default';

  const count = getLocalesErrorCount({
    isNBAFeatureEnabled: isNextBestActionEnabled,
    allContentErrors,
    nbaLocalesErrors,
  });

  const errorCount = hideErrorCount ? 0 : count;

  const [updateDefaultLocale] = useUpdateDefaultLocale({
    onError: (errorMessages) => {
      errorMessages.forEach((errorMessage) => dispatch(addNegativeToast(errorMessage)));
    },
    onSuccess: (resp) => {
      const message = `Default locale successfully changed to ${resp?.defaultLocale?.tag}`;
      dispatch(addPositiveToast(message));
      refetchLocales();
    },
  });

  const selectedValue = locale || templateDefaultLocaleTag || ''; // Return Default Locale if none is set

  const localeOptions = useLocaleOptions({
    allContentErrors,
    allProjectLocales,
    activeTemplateLocales,
    isReadOnly,
    isAssociatedWithActiveExperiment,
    templateDefaultLocaleTag,
    hideErrorCount,
    activeWhatsAppTemplateLocales,
    invalidWhatsAppLocales,
    validWhatsAppLocales,
  });

  const canDelete = selectedValue !== templateDefaultLocaleTag;
  const canChangeDefault =
    isTemplateDefaultLocaleModifiable ||
    (projectDefaultLocaleExistsInTemplate && projectDefaultLocaleTag !== templateDefaultLocaleTag);

  const handleSelect: OnSelectHandler = useCallback(
    ([localeTag]) => {
      // note: checks are only valid for single editor environment
      const selectedExists = !!activeTemplateLocales.find((tl) => tl.tag === localeTag);
      const selectedIsEligible = !!allProjectLocales.find((pl) => pl.tag === localeTag);
      if (selectedExists) {
        setLocale(localeTag);
        revalidate(localeTag);
      } else if (selectedIsEligible && !isReadOnly && !isLoading) {
        // if the locale switcher is not read only or template/locales are not loading, add the locale to the template
        addLocale(localeTag);
      }
    },
    [
      activeTemplateLocales,
      allProjectLocales,
      isLoading,
      isReadOnly,
      addLocale,
      revalidate,
      setLocale,
    ],
  );

  const handleChangeDefault = (variables: Variables) => {
    updateDefaultLocale({ variables });
  };

  const { options, handleFilter } = useFilterLocales(localeOptions, defaultSuffix);

  const hasSubmittedWhatsAppLocale = useMemo(
    () =>
      templateType === TemplateType.BusinessInitiated &&
      !!activeWhatsAppTemplateLocales?.some(
        (activeWhatsAppTemplateLocale) =>
          activeWhatsAppTemplateLocale?.metaApprovalStatus &&
          activeWhatsAppTemplateLocale?.metaApprovalStatus !==
            MetaApprovalStatusDefinition.NotSubmitted,
      ),
    [activeWhatsAppTemplateLocales, templateType],
  );

  const localeActionMenuOptions = useLocaleActionMenuOptions({
    handleShowDefaultLocaleModal,
    handleShowDeleteModal,
    canDelete,
    canChangeDefault,
    isDisabled,
    isAssociatedWithActiveExperiment,
    hasSubmittedWhatsAppLocale,
  });

  const allContentLocaleErrors = allContentErrors.map((error) => {
    return error === '' ? templateDefaultLocaleTag : error;
  });

  const allContentLocaleErrorNames = allContentLocaleErrors.join(', ');
  const nbaErroredLocaleNames = nbaLocalesErrors.join(', ');

  const Trigger = useMemo(() => {
    return LocaleSwitcherTrigger({ templateDefaultLocale, errorCount });
  }, [templateDefaultLocale, errorCount]);

  const tooltipErrorContent = useTooltipErrorContent(
    nbaLocalesErrors,
    nbaErroredLocaleNames,
    allContentLocaleErrorNames,
  );

  return (
    <SwitchContainer id="locale-switch-container" data-test="locale-switch-container">
      <ComboButton $isReadOnly={isReadOnly}>
        <LeftTooltip
          automaticallyAvoidCollisions
          isEnabled={errorCount > 0}
          tooltipContent={tooltipErrorContent}
        >
          <BasicFilterSelect
            width={'380px'}
            showCategoryDivider
            automaticallyAvoidCollisions
            defaultValues={[selectedValue]}
            horizontalAlignment={horizontalAlignment || ALIGNMENT.RIGHT}
            id="locale-switcher-dropdown"
            isDisabled={isDisabled || isSwitcherDisabled}
            onSelect={handleSelect}
            showAllCategories
            onSearchChange={debounce(handleFilter, 300)}
            disableInternalFiltering
            options={options}
            placeholder={t('input.localeSwitcher.placeholder')}
            inputContent={{
              filterPlaceholder: t('input.localeSwitcherFilter.placeholder'),
            }}
            Trigger={Trigger as ComponentType<React.PropsWithChildren<SelectValueDisplayProps>>}
            size={BASIC_SELECT_SIZES.LARGE}
            categoryLabels={getCategoryLabels(isWhatsAppInfobipLocalesEnabled)}
          />
        </LeftTooltip>

        {!isReadOnly && (
          <>
            {isLocaleImprovementsEnabled && !isExperimentFlow ? (
              <ActionMenu
                align={MENU_ALIGNMENTS.RIGHT}
                options={localeActionMenuOptions}
                id="default-locale-menu"
              >
                <StyledOverflowButton
                  isDisabled={isDisabled}
                  onClick={() => null}
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  <Settings height={18} width={18} />
                </StyledOverflowButton>
              </ActionMenu>
            ) : (
              <Tooltip
                tooltipContent={t(`tooltip.${canDelete ? 'deleteLocale' : 'disabledDelete'}`)}
              >
                <StyledOverflowButton
                  data-test="locale-switcher-delete-modal-opener-button"
                  isDisabled={!canDelete || isDisabled}
                  onClick={() => handleShowDeleteModal(true)}
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  <Delete height={16} width={16} />
                </StyledOverflowButton>
              </Tooltip>
            )}
          </>
        )}
      </ComboButton>

      {showDefaultLocaleModal && (
        <DefaultLocaleModal
          closeModal={() => handleShowDefaultLocaleModal(false)}
          onDefaultChanged={(variables) => {
            handleChangeDefault(variables);
            handleShowDefaultLocaleModal(false);
          }}
          templateId={templateId}
        />
      )}

      {showDeleteModal && (
        <DeleteLocaleModal
          locale={locale}
          onDelete={() => {
            deleteLocale(selectedValue);
            handleShowDeleteModal(false);
          }}
          closeModal={() => handleShowDeleteModal(false)}
        />
      )}
    </SwitchContainer>
  );
};
